import { useEffect, useState } from 'react';
import { namesDiffer } from '@/lib/nameMatch';
import { NameMatchBasisSelect } from '@/components/NameMatchBasisSelect';
import { useRows } from '@/hooks/useResource';
import type { FarmerRow, KycRow } from '@/types/records';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, X } from 'lucide-react';
import { Drawer, Field, Select, todayIso, useFormErrors } from '@/components/live';
import { callRpc, saveRow } from '@/api/resources';
import { partyOptions, referenceValues } from '@/api/lookups';
import { uploadDocument } from '@/api/documents';
import { DropZone } from '@/components/DropZone';
import { useAuth } from '@/auth/AuthProvider';
import { isAdmin } from '@/auth/session';
import { toast } from '@/lib/toast';
import { renewalStart } from '@/lib/renewals';
import { kycTypeExpires } from '@/lib/kycExpiry';
import { isSupabaseConfigured } from '@/lib/supabase';

/** What the `kyc-documents` bucket admits, minus HEIC, which most desktop browsers cannot show. */
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp';
const MAX_BYTES = 10 * 1024 * 1024;
const OTHER = '__other__';

const EMPTY = {
  party_id: '', kyc_type_id: '', document_number: '', issued_by: '',
  issued_on: '', valid_from: todayIso(), valid_to: '',
  name_on_document: '', name_match_basis: '', name_match_remarks: '',
};

/**
 * One KYC record: which document (Record Type), its number, its validity and the scan.
 *
 * A farmer brings several - Aadhaar, a land record, a bank passbook - so KYC is a list of child
 * records under the party, and this drawer adds one at a time. It is used both from the KYC
 * register (any party) and from inside a farmer's record (party fixed).
 *
 * The scan is chosen in the same form, but stored AFTER the record is saved: the storage key
 * begins with the organisation and the owning party, and the record is what the file attaches
 * to. If the upload fails the record still exists and says "Attach" in the grid, so nothing is
 * silently lost.
 */
/**
 * A KYC record typed in before the farmer - and so the party - exists (owner, 2026-09-27: "full kyc
 * in farmers registration tab?"). Held with the farmer form and written by `saveKycRecord` once the
 * farmer is saved.
 */
export interface PendingKyc {
  key: string;
  typeLabel: string;
  values: typeof EMPTY;
  file: File | null;
  printedDiffers: boolean;
}

/** Writes a KYC record for a party: the record, the printed name, then the file. */
export async function saveKycRecord(partyId: string, organizationId: string | null, item: Omit<PendingKyc, 'key' | 'typeLabel'>, renewingId?: string | null,
                                    roleContext = 'farmer') {
  const form = item.values;
  const recordId = await callRpc<string>('fn_create_kyc_record', {
    p_renewal_parent_id: renewingId ?? null,
    p_party_id: partyId,
    p_kyc_type_id: form.kyc_type_id,
    p_document_number: form.document_number.trim(),
    p_valid_to: form.valid_to || null,
    p_issued_by: form.issued_by.trim() || null,
    p_issued_on: form.issued_on || null,
    p_valid_from: form.valid_from || null,
    p_party_role_context: roleContext,
  });
  // The name as printed on the card (0078); a difference opens a name verification.
  if (form.name_on_document.trim()) {
    await callRpc('fn_set_kyc_document_name', {
      p_kyc_record_id: recordId, p_name: form.name_on_document.trim(),
      p_basis: item.printedDiffers ? form.name_match_basis || null : null,
      p_remarks: item.printedDiffers ? form.name_match_remarks.trim() || null : null,
    });
  }
  if (item.file && organizationId) {
    try {
      const document = await uploadDocument({
        file: item.file, bucket: 'kyc-documents', organizationId,
        ownerId: partyId, issuedDate: form.issued_on || null, expiryDate: form.valid_to || null,
      });
      await saveRow('party_kyc_records', recordId, { document_id: document.id });
    } catch (error) {
      toast.error(`Record saved, but the file was not attached: ${
        error instanceof Error ? error.message : 'upload failed'}. Use "Attach" on the record.`);
    }
  }
  return recordId;
}

export function KycRecordDrawer({
  open,
  onClose,
  partyId,
  partyLabel,
  renewing,
  inline = false,
  onQueue,
  referenceName,
  roleContext = 'farmer',
}: {
  /** Whose KYC this is on the record: a farmer's, or a representative's own (owner, 2026-09-28). */
  roleContext?: string;
  /** Drawn in place, not as a drawer - the farmer form's KYC tab. It stays open after a save. */
  inline?: boolean;
  /** No party yet (a farmer being registered): the record is handed back to be saved with it. */
  onQueue?: (item: PendingKyc) => void;
  /** The name to compare the printed one with when there is no party record to read it from. */
  referenceName?: string | null;
  open: boolean;
  onClose: () => void;
  /** Fixes the party - used from a farmer's own record. */
  partyId?: string;
  partyLabel?: string;
  /** Renewing this record (0089): party and type are fixed and carried over, dates start fresh. */
  renewing?: KycRow | null;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { errors, check, clear } = useFormErrors();
  const [form, setForm] = useState({ ...EMPTY, party_id: partyId ?? '' });

  // Opened to renew: the party, the record type and the issuing authority carry over; the number
  // is retyped (only its last four characters are kept, so it cannot be filled in), and the new
  // validity starts where the old one ends.
  useEffect(() => {
    if (!open || !renewing) return;
    setForm({
      ...EMPTY,
      party_id: renewing.party_id,
      kyc_type_id: renewing.kyc_type_id,
      issued_by: renewing.issued_by ?? '',
      valid_from: renewalStart(renewing.valid_to, todayIso()),
    });
  }, [open, renewing]);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [newType, setNewType] = useState('');

  const admin = isAdmin(profile);
  // The name on file to compare the printed name with (0078).
  const partyFarmer = useRows<FarmerRow>('farmers', { filters: { party_id: form.party_id } }, (open || inline) && Boolean(form.party_id));
  const partyIdName = referenceName ?? (partyFarmer.data?.[0] ? (partyFarmer.data[0].name_as_per_id || partyFarmer.data[0].full_name) : partyLabel ?? null);
  const printedDiffers = Boolean(partyIdName) && namesDiffer(form.name_on_document, partyIdName);
  // Issued by follows the record type (owner, 2026-09-21): the type's own authorities, the one
  // that is always right pre-selected (Aadhaar -> UIDAI), and "Other..." for a free entry.
  const [otherIssuer, setOtherIssuer] = useState(false);

  const parties = useQuery({
    queryKey: ['picker', 'parties'], queryFn: () => partyOptions(),
    enabled: isSupabaseConfigured && (open || inline) && !partyId && !onQueue,
  });
  const kycTypes = useQuery({
    queryKey: ['ref', 'kyc_type'], queryFn: () => referenceValues('kyc_type'),
    enabled: isSupabaseConfigured && (open || inline),
  });

  const chosenType = (kycTypes.data ?? []).find((row) => row.id === form.kyc_type_id) as
    | { value_code?: string; attributes?: { issuers?: string[]; default_issuer?: string | null } } | undefined;
  const issuers = chosenType?.attributes?.issuers ?? [];
  const expires = kycTypeExpires(chosenType?.value_code);

  function chooseType(typeId: string) {
    const type = (kycTypes.data ?? []).find((row) => row.id === typeId) as
      | { attributes?: { issuers?: string[]; default_issuer?: string | null } } | undefined;
    setOtherIssuer(false);
    setForm((current) => ({ ...current, kyc_type_id: typeId, issued_by: type?.attributes?.default_issuer ?? '' }));
  }

  function reset() {
    clear();
    setForm({ ...EMPTY, party_id: partyId ?? '' });
    setFile(null);
    setAddingType(false);
    setNewType('');
    setOtherIssuer(false);
  }
  function close() {
    reset();
    onClose();
  }

  function pickFile(chosen: File | undefined) {
    if (!chosen) return;
    if (!ACCEPT.split(',').includes(chosen.type)) {
      toast.error('Attach a PDF or a JPEG/PNG photograph.');
      return;
    }
    if (chosen.size > MAX_BYTES) {
      toast.error('That file is larger than 10 MB.');
      return;
    }
    setFile(chosen);
  }

  async function addType() {
    const label = newType.trim();
    if (!label) return;
    try {
      const id = await callRpc<string>('fn_add_reference_value', { p_set_key: 'kyc_type', p_label: label });
      await queryClient.invalidateQueries({ queryKey: ['ref', 'kyc_type'] });
      chooseType(id);
      setAddingType(false);
      setNewType('');
      toast.success(`Added "${label}".`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That record type could not be added.');
    }
  }

  async function save() {
    const ok = check({
      party_id: form.party_id || onQueue ? undefined : 'Whose document is this?',
      kyc_type_id: form.kyc_type_id ? undefined : 'Which record type is it?',
      document_number: form.document_number.trim() ? undefined : 'Enter the number on the document.',
      valid_to: form.valid_to || !expires ? undefined : 'When does it expire? A licence or passport runs out.',
      valid_from: expires && form.valid_from && form.valid_to && form.valid_to < form.valid_from ? 'It cannot expire before it starts.' : undefined,
    });
    if (!ok) return;
    // A document that never expires carries no validity at all (owner, 2026-09-28: "KYC docs doesnt
    // contain validity, you remove both fields?"): nothing typed there earlier is kept.
    const values = expires ? form : { ...form, valid_from: '', valid_to: '' };
    if (onQueue && !form.party_id) {
      onQueue({
        key: crypto.randomUUID(), values: { ...values }, file, printedDiffers,
        typeLabel: (kycTypes.data ?? []).find((row) => row.id === form.kyc_type_id)?.label ?? 'KYC record',
      });
      toast.success('KYC record added. It is saved with the farmer.');
      reset();
      return;
    }

    setSaving(true);
    try {
      await saveKycRecord(form.party_id, profile?.organization?.id ?? null, { values, file, printedDiffers }, renewing?.id, roleContext);
      await queryClient.invalidateQueries({ queryKey: ['party_kyc_records'] });
      await queryClient.invalidateQueries({ queryKey: ['farmers'] });
      await queryClient.invalidateQueries({ queryKey: ['name_verifications'] });
      toast.success('KYC record saved. Someone other than you has to verify it.');
      if (inline) reset(); else close();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const body = (
    <>
      {renewing && (
        <p className="renew-note">
          The document may keep its number - type it in full again, as only its last four characters
          are kept. The record being renewed stays on file until this one is verified.
        </p>
      )}
      {!partyId && !renewing && !onQueue && (
        <Field error={errors.party_id} label="Party" required>
          <Select onChange={(value) => setForm({ ...form, party_id: value })}
                  options={parties.data ?? []}
                  placeholder={parties.isLoading ? 'Loading...' : 'Select party'}
                  value={form.party_id} />
        </Field>
      )}

      <Field error={errors.kyc_type_id} label="Record type" required
             hint={admin ? undefined : 'A missing type can be added by a Project Admin.'}>
        <div className="select-with-add">
          <Select disabled={Boolean(renewing)} onChange={chooseType}
                  options={(kycTypes.data ?? []).map((row) => ({ id: row.id, label: row.label }))}
                  placeholder={kycTypes.isLoading ? 'Loading...' : 'Select record type'}
                  value={form.kyc_type_id} />
          <button aria-label="Add a record type" disabled={!admin} onClick={() => setAddingType(true)}
                  title={admin ? 'Add a record type' : 'Only a Project Admin can add record types.'}
                  type="button">
            <Plus size={16} />
          </button>
        </div>
      </Field>
      {addingType && (
        <div className="inline-add">
          <input autoFocus onChange={(event) => setNewType(event.target.value)}
                 onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void addType(); } }}
                 placeholder="e.g. Voter ID, Patta, Bank passbook" value={newType} />
          <button className="primary" disabled={!newType.trim()} onClick={() => void addType()} type="button">Add</button>
          <button aria-label="Cancel" onClick={() => { setAddingType(false); setNewType(''); }} type="button"><X size={16} /></button>
        </div>
      )}

      <Field error={errors.document_number} hint="Only the last four characters are kept. The full number is fingerprinted to catch duplicates, never stored."
             label="Document number" required>
        <input onChange={(event) => setForm({ ...form, document_number: event.target.value })}
               value={form.document_number} />
      </Field>
      <Field hint="Exactly as printed on the document - spelling, initials and all." label="Name as printed on the document">
        <input onChange={(event) => setForm({ ...form, name_on_document: event.target.value })}
               placeholder={partyIdName ?? undefined} value={form.name_on_document} />
      </Field>
      {printedDiffers && (
        <div className="name-match">
          <p className="muted" style={{ margin: 0 }}>
            The document reads <strong>{form.name_on_document}</strong>; the name as per ID card on file is <strong>{partyIdName}</strong>.
            Saving opens a <strong>name verification</strong> for someone else to accept. Add what confirms it, if you know.
          </p>
          <Field label="Confirmed by">
            <NameMatchBasisSelect onChange={(value) => setForm((now) => ({ ...now, name_match_basis: value }))} recorded={form.name_on_document} reference={partyIdName} value={form.name_match_basis} />
          </Field>
          <Field label="Remarks">
            <input onChange={(event) => setForm({ ...form, name_match_remarks: event.target.value })}
                   placeholder="e.g. Voter ID and ration card both read 'Md. Rahim'" value={form.name_match_remarks} />
          </Field>
        </div>
      )}
      <Field label="Issued by">
        {issuers.length > 0 && !otherIssuer ? (
          <Select
            onChange={(value) => {
              if (value === OTHER) { setOtherIssuer(true); setForm({ ...form, issued_by: '' }); return; }
              setForm({ ...form, issued_by: value });
            }}
            options={[...issuers.map((name) => ({ id: name, label: name })), { id: OTHER, label: 'Other...' }]}
            placeholder="Select issuing authority"
            value={form.issued_by}
          />
        ) : (
          <input onChange={(event) => setForm({ ...form, issued_by: event.target.value })}
                 placeholder="Issuing authority" value={form.issued_by} />
        )}
      </Field>
      <div className="field-row">
        <Field label="Issued on">
          <input onChange={(event) => setForm({ ...form, issued_on: event.target.value })} type="date" value={form.issued_on} />
        </Field>
      </div>
      {/* Validity only for a document that runs out: a driving licence or passport. */}
      {expires && (
        <div className="field-row">
          <Field error={errors.valid_from} hint="From when the document is in force - usually the issue date." label="Valid from">
            <input onChange={(event) => setForm({ ...form, valid_from: event.target.value })} type="date" value={form.valid_from} />
          </Field>
          <Field error={errors.valid_to} label="Valid to" required>
            <input min={form.valid_from || undefined} onChange={(event) => setForm({ ...form, valid_to: event.target.value })} type="date" value={form.valid_to} />
          </Field>
        </div>
      )}

      <Field hint="PDF or JPEG/PNG, up to 10 MB. Stored privately." label="Document file">
        <DropZone accept={ACCEPT} buttonLabel={file ? 'Choose another' : 'Choose file'}
                  label={file ? 'Drop another to replace, or' : 'Drag the PDF or photo here, or'}
                  onFile={pickFile}>
          {file && (
            <span className="file-chosen">
              <FileText size={16} /> {file.name}
              <button aria-label="Remove file" onClick={() => setFile(null)} type="button"><X size={14} /></button>
            </span>
          )}
        </DropZone>
      </Field>
    </>
  );

  if (inline) {
    return (
      <div className="inline-form kyc-inline">
        {body}
        <div className="inline-form-actions">
          <button className="cancel" onClick={reset} type="button">Clear</button>
          <button className="primary" disabled={saving} onClick={() => void save()} type="button">
            {saving ? 'Saving...' : onQueue && !form.party_id ? 'Add KYC record' : 'Save KYC record'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Drawer
      footer={
        <>
          <button className="cancel" onClick={close} type="button">Cancel</button>
          <button className="primary" disabled={saving} onClick={() => void save()} type="button">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }
      onClose={close}
      open={open}
      subtitle={renewing
        ? `Renewing ${renewing.kyc_type_label ?? 'the record'}${renewing.masked_document_no ? ` ${renewing.masked_document_no}` : ''}. Someone other than you has to verify it.`
        : partyLabel ? `For ${partyLabel}. Someone other than you has to verify it.`
                     : 'Someone other than you has to verify it.'}
      title={renewing ? 'Renew KYC record' : 'Add KYC record'}
    >
      {body}
    </Drawer>
  );
}
